import React, { useState } from 'react';
import { 
  Truck, Building2, Sparkles, Bike, Radio, Landmark, 
  FileText, TruckIcon, Instagram, Newspaper, Tv, Camera,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { services } from '../data/mock';

export const Services = () => {
  const [expandedService, setExpandedService] = useState(null);

  const iconMap = {
    Truck, Building2, Sparkles, Bike, Radio, Landmark,
    FileText, TruckIcon, Instagram, Newspaper, Tv, Camera
  };

  const toggleService = (id) => {
    setExpandedService(expandedService === id ? null : id);
  };

  return (
    <section id="services" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-blue-600">Complete</span> Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            50+ advertising services ensuring low-budget, high-impact campaigns for every business
          </p>
        </div>

        {/* Services Header Image - Real Campaign Examples */}
        <div className="mb-12 grid md:grid-cols-3 gap-6">
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="https://customer-assets.emergentagent.com/job_insmart-media/artifacts/yj6g8d7a_Gemini_Generated_Image_fhbqqofhbqqofhbq.png"
              alt="E-Rickshaw & Tricycle Branding Services"
              width="400"
              height="256"
              loading="lazy"
              decoding="async"
              className="w-full h-64 object-cover"
            />
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="https://customer-assets.emergentagent.com/job_insmart-media/artifacts/2rr0ek09_Gemini_Generated_Image_fhbqqofhbqqofhbq%20%281%29.png"
              alt="Road Arch Welcome Gate Branding"
              width="400"
              height="256"
              loading="lazy"
              decoding="async"
              className="w-full h-64 object-cover"
            />
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="https://customer-assets.emergentagent.com/job_insmart-media/artifacts/jr1ipvbn_Mobile%20vhan.jpeg"
              alt="Mobile Van & LED Van Branding"
              width="400"
              height="256"
              loading="lazy"
              decoding="async"
              className="w-full h-64 object-cover"
            />
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => {
            const Icon = iconMap[service.icon];
            const isExpanded = expandedService === service.id;

            return (
              <Card 
                key={service.id}
                className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-blue-600 cursor-pointer"
                onClick={() => toggleService(service.id)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="bg-blue-100 p-3 rounded-lg group-hover:bg-blue-600 transition-colors">
                      {Icon && <Icon className="text-blue-600 group-hover:text-white transition-colors" size={28} />}
                    </div>
                    <button className="text-gray-400 hover:text-blue-600 transition-colors">
                      {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                    </button>
                  </div>
                  <CardTitle className="text-xl mt-4 group-hover:text-blue-600 transition-colors">
                    {service.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="space-y-4 pt-4 border-t animate-fade-in">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Key Features:</h4>
                        <ul className="space-y-2">
                          {service.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                              <span className="text-blue-600 mt-1">•</span>
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-blue-700 font-medium">
                          ✓ {service.benefits}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Additional Services Banner */}
        <div className="mt-16 bg-blue-600 rounded-2xl p-8 md:p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">Looking for Custom Solutions?</h3>
          <p className="text-xl mb-6 text-blue-100">
            We offer 50+ specialized advertising services tailored to your business needs
          </p>
          <Button 
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            size="lg"
            className="bg-white text-blue-600 hover:bg-blue-50"
          >
            Discuss Your Requirements
          </Button>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { 
            opacity: 0;
            transform: translateY(-10px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </section>
  );
};
